<template>
  <div
    class="modal"
    :class="{ 'is-active': value }">

    <div class="modal-background"
      @click="close()">
    </div>
    
    <div class="modal-content">
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">
            Assign Parts to Assembly Sheet
          </p>
          <button
            class="delete"
            aria-label="close"
            @click="close(true)">
          </button>
        </header>

        <v-form 
          class="modal-card-body"
          :key="addingNew" 
          :valid.sync="valid">

          <template v-if="!addingNew">
            <div class="field" >
              <label class="label">Assembly Sheet*</label>
              <v-select 
                required
                :options="assemblySheets"
                item-text="assemblysheet_no"
                item-value="id"
                v-model="assemblySheetId"
              />
            </div>
            <button 
              class="button is-primary"
              @click="addingNew = true">
              Assign Parts to a New Assembly Sheet
            </button>
          </template>

          <template v-else>
            <div class="field">
              <label class="label">Assembly Sheet Condition</label>
              <v-text-field
                placeholder="Enter assembly condition"
                v-model="assemblySheet.condition">
              </v-text-field>
            </div>
            <div class="field" >
              <label class="label">Assembly Sheet Description</label>
              <v-text-field
                placeholder="Enter assembly instructions"
                v-model="assemblySheet.desc">
              </v-text-field>
            </div>
            <div class="field" >
              <label class="label">Total Number of Assembled Parts*</label>
              <v-text-field
                required
                placeholder="Total number of assembled parts"
                v-model="assemblySheet.finished_qty">
              </v-text-field>
            </div>
            <div class="field">
              <label class="label">Assembly Sheet Upload</label>
              <v-file-upload
                file-type-validator=".*"
                v-model="attachment"
                has-name>
              </v-file-upload>
            </div>
          </template>
          <table class="table is-fullwidth mt-5">
            <thead>
              <tr>
                <th>Part #</th>
                <th>Part Quantity</th>
                <th>Quantity to Assemble</th>
                <th>Assembly Details</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="part in parts" :key="part.id">
                <td>{{ part.part_no }}</td>
                <td>{{ part.quantity | round(0) }}</td>
                <td>
                  <v-text-field 
                    required
                    type="number"
                    placeholder="Quantity to add to assembly sheet"
                    v-model="part.qty_to_assemble">
                  </v-text-field>
                </td>
                <td>
                  <v-text-field 
                    placeholder="Enter part assembly details"
                    v-model="part.section">
                  </v-text-field>
                </td>
              </tr>
            </tbody>
          </table>

        </v-form>

        <footer class="modal-card-foot">
          <button 
            :disabled="!valid && !addingNew"
            class="button is-primary" 
            @click.stop="assignPartsToAssembly()">
            Save
          </button>
          <button 
            class="button" 
            @click.stop="close(true)">
            Cancel
          </button>
        </footer>

      </div>
    </div>
  </div>
</template>

<script>
import VSelect from '@/components/base/VSelect'
import VTextField from '@/components/base/VTextField'
import VForm from '@/components/base/VForm'
import VFileUpload from '@/components/base/VFileUpload'

import { mapActions, mapMutations } from 'vuex'

export default {
  props: {
    value: {
      type: Boolean
    },
    assemblySheets: {
      type: Array
    },
    projectId: {
      type: [String, Number],
      required: true
    },
    parts: {
      type: Array,
      required: true
    }
  },
  components: { VTextField, VSelect, VForm, VFileUpload },
  data() {
    return {
      assemblySheetId: null,
      assemblySheet: {
        condition: null,
        id: null,
        desc: null,
        details: []
      },
      attachment: {
        file: null,
        name: null,
        url: null
      },
      valid: false,
      addingNew: false
    }
  },
  computed: {
    partsFormatted() {
      return this.parts.map(d => {
        return {
          id: null, 
          cutsheetpart_id: d.id, 
          part_no: d.part_no, 
          qty_of_parts: d.qty_to_assemble,
          section: d.section
        }
      })
    }
  },
  watch: {
    value(state) {
      if (state) {
        if (this.assemblySheets.length == 0) {
          this.addingNew = true;
        } else {
          this.addingNew = false;
        }
      }
    }
  },
  methods: {
    ...mapActions("assembly", [
      "addAssemblySheet", "addParts", "addAttachment", "getAssemblySheets"
    ]),
    ...mapMutations("app", ["setNotification"]),
    assignPartsToAssembly() {
      if (this.addingNew) {
        this.assemblySheet.details = this.partsFormatted;
        this.addAssemblySheet({ 
          data: this.assemblySheet, 
          parameters: { project_id: this.projectId } 
        }).then(
          resp => {
            if (this.attachment) {
              let formData = new FormData();
              formData.append(
                'attachment', 
                this.attachment.file, 
                this.attachment.file.name
              );

              this.addAttachment({ 
                assemblySheetId: resp.data.id,  
                attachment: formData
              }).then(() => {
                this.notifySuccess(
                  'Parts successfully assigned to new assembly sheet'
                );
                this.close(true);
              });
            }else {
              this.close(true);
            }
          }
        )
      } else {
        let selected = this.assemblySheets.filter(
          d => d.id == this.assemblySheetId
        )[0];

        let details = [
          ...selected.details, ...this.partsFormatted
        ];

        this.addParts(
          { assemblySheetId: this.assemblySheetId, data: { details: details } }
        ).then(
          () => {
            this.notifySuccess(
              `Parts successfully assigned to assembly sheet ${selected.assemblysheet_no}`
            );
            this.close(true);
          }
        )
      }
    },
    notifySuccess(msg) {
      this.getAssemblySheets();
      
      this.setNotification({
        status: true,
        type: 'success',
        message: msg
      });
    },
    close(clear=false) {
      this.$emit('input', false);
      if (clear) {
        this.clearData();
      }
    },
    clearData() {
      Object.assign(this.$data, this.$options.data.call(this));
    }
  }
}
</script>

<style scoped>
.table td {
  vertical-align: middle;
}
</style>